import { useEffect, useState } from "react";
import { useAuth } from "../auth-context";
import { Pill } from "../components/Pill";
import { api } from "../api";

export function SettingsPage() {
  const { profile, refresh } = useAuth();
  const ws = profile.workspace;
  const [saving, setSaving] = useState<string | null>(null);
  if (!ws) return <div className="sg-empty">Loading…</div>;

  const mode: "suggest" | "auto" | "block_if_unsafe" = ws.settings?.anonymisationMode ?? "suggest";
  const [bizContext, setBizContext] = useState<string>(ws.settings?.businessContext ?? "");
  const [savedBiz, setSavedBiz] = useState<string>(ws.settings?.businessContext ?? "");

  async function saveBusinessContext() {
    if (bizContext === savedBiz) return;
    setSaving("business");
    try {
      await api.updateWorkspaceSettings({ settings: { ...ws.settings, businessContext: bizContext } });
      await refresh();
      setSavedBiz(bizContext);
    } finally { setSaving(null); }
  }

  async function setMode(next: "suggest" | "auto" | "block_if_unsafe") {
    setSaving("mode");
    try {
      await api.updateWorkspaceSettings({ settings: { ...ws.settings, anonymisationMode: next } });
      await refresh();
    } finally { setSaving(null); }
  }

  async function toggleSetting(key: "revealRequiresConfirmation" | "allowUserContinueOverride" | "autoGenerateSafeVersion" | "failClosedOnEvaluationError") {
    setSaving(key);
    try {
      await api.updateWorkspaceSettings({ settings: { ...ws.settings, [key]: !ws.settings[key] } });
      await refresh();
    } finally { setSaving(null); }
  }

  // Bootstrap-super-admin probe.
  // We always render this card while the user is not yet super_admin so
  // the founder can see WHY they can't promote. The button is only
  // enabled when the env-configured email matches the caller AND no
  // super_admin exists yet.
  const [bootstrap, setBootstrap] = useState<{ isCandidate: boolean; bootstrapOpen: boolean; myRole?: string } | null>(null);
  const [bootstrapErr, setBootstrapErr] = useState<string | null>(null);
  const [promoting, setPromoting] = useState(false);
  const [promoMsg, setPromoMsg] = useState<string | null>(null);
  useEffect(() => {
    api.getBootstrapStatus()
      .then((r) => { setBootstrap(r); setBootstrapErr(null); })
      .catch((ex: any) => { setBootstrapErr(ex?.message ?? "Could not check bootstrap status."); setBootstrap(null); });
  }, []);
  async function promote() {
    setPromoting(true);
    setPromoMsg(null);
    try {
      const r = await api.bootstrapSuperAdmin();
      setPromoMsg(r.alreadyPromoted ? "You are already a super admin. Reload the page." : "Promoted. Reloading…");
      setTimeout(() => window.location.reload(), 1200);
    } catch (ex: any) {
      setPromoMsg(ex?.message ?? "Could not promote.");
    } finally { setPromoting(false); }
  }

  return (
    <>
      <header className="sg-page-header">
        <div>
          <h1 className="sg-page-title">Settings</h1>
          <div className="sg-page-sub">Workspace Configuration · Storage Posture</div>
        </div>
      </header>

      {bootstrap?.myRole !== "super_admin" && (
        <div className="sg-panel" style={{ marginBottom: 24, borderColor: "var(--sg-blue, #2c4a6a)" }}>
          <div className="sg-panel-pad">
            <div className="sg-section-label">Founder bootstrap</div>
            <div className="sg-muted" style={{ fontSize: 13, marginBottom: 10 }}>
              {bootstrapErr ? (
                <span>Could not reach the bootstrap check: <code>{bootstrapErr}</code></span>
              ) : !bootstrap ? (
                "Checking…"
              ) : !bootstrap.isCandidate ? (
                <>This account is not the configured bootstrap admin email. The server expects a specific email to be the founder; yours does not match. Update <code>VENTRIN_BOOTSTRAP_ADMIN_EMAIL</code> on the server to your sign-in email and redeploy.</>
              ) : !bootstrap.bootstrapOpen ? (
                "A super_admin already exists. The bootstrap door is closed."
              ) : (
                "You are the configured bootstrap admin and no super_admin exists yet. Promote yourself once. After this, the bootstrap door closes and Growth OS becomes available from the profile menu."
              )}
            </div>
            <button
              className="sg-btn sg-btn-primary sg-btn-sm"
              onClick={promote}
              disabled={promoting || !bootstrap?.isCandidate || !bootstrap?.bootstrapOpen}
            >
              {promoting ? "Promoting…" : "Promote me to super_admin"}
            </button>
            {promoMsg && <div className="sg-muted" style={{ fontSize: 12.5, marginTop: 8 }}>{promoMsg}</div>}
          </div>
        </div>
      )}

      <div className="sg-section-label">Business Context</div>
      <div className="sg-panel" style={{ marginBottom: 24 }}>
        <div className="sg-panel-pad">
          <div className="sg-muted" style={{ fontSize: 12.5, marginBottom: 10 }}>
            Describe your business in one line. The anonymiser uses this to choose natural language that fits your industry (e.g. "boutique corporate law firm", "NHS GP practice", "series-B fintech").
          </div>
          <div className="sg-row" style={{ gap: 10 }}>
            <input
              className="sg-input"
              style={{ flex: 1 }}
              placeholder="e.g. Mid-market commercial law firm specialising in M&A"
              value={bizContext}
              onChange={(e) => setBizContext(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") saveBusinessContext(); }}
              onBlur={saveBusinessContext}
              disabled={saving === "business"}
            />
            <button
              className="sg-btn sg-btn-primary sg-btn-sm"
              disabled={saving === "business" || bizContext === savedBiz}
              onClick={saveBusinessContext}
            >
              {saving === "business" ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>

      <div className="sg-section-label">Auto-Anonymisation</div>
      <div className="sg-panel" style={{ marginBottom: 30 }}>
        <div className="sg-panel-pad">
          <div className="sg-muted" style={{ fontSize: 12.5, marginBottom: 14 }}>
            Choose how Ventrin handles flagged prompts. Changes apply to every user in the workspace.
          </div>
          <div className="sg-grid sg-grid-3">
            <ModeCard
              active={mode === "suggest"} disabled={!!saving}
              onClick={() => setMode("suggest")}
              title="Suggest" kind="neutral"
              body="Always show the safe version. User picks Fix & Resend, Edit, or Continue."
            />
            <ModeCard
              active={mode === "auto"} disabled={!!saving}
              onClick={() => setMode("auto")}
              title="Auto" kind="rewrite"
              body="High-confidence rewrites are applied silently. Low-confidence prompts fall back to suggest."
            />
            <ModeCard
              active={mode === "block_if_unsafe"} disabled={!!saving}
              onClick={() => setMode("block_if_unsafe")}
              title="Block If Unsafe" kind="block"
              body="Block prompts the anonymiser isn't confident it can rewrite cleanly."
            />
          </div>
        </div>
      </div>

      <div className="sg-grid sg-grid-2">
        <div className="sg-panel">
          <div className="sg-panel-head"><div className="sg-panel-title">Workspace</div></div>
          <div className="sg-panel-pad sg-stack">
            <Row label="Name" value={ws.name} />
            <Row label="Vertical" value={ws.verticalType} />
            <Row label="Plan" value={ws.plan} />
            <Row label="Created" value={new Date(ws.createdAt).toLocaleDateString()} />
          </div>
        </div>
        <div className="sg-panel">
          <div className="sg-panel-head"><div className="sg-panel-title">Prompt Storage</div></div>
          <div className="sg-panel-pad sg-stack">
            <Row label="Mode" value={<Pill kind="neutral">{ws.promptStorageMode}</Pill>} />
            <div className="sg-spread">
              <span className="sg-muted" style={{ fontSize: 12 }}>Reveal Requires Confirmation</span>
              <button className="sg-btn sg-btn-sm" disabled={saving === "revealRequiresConfirmation"} onClick={() => toggleSetting("revealRequiresConfirmation")}>
                {ws.settings.revealRequiresConfirmation ? "Yes" : "No"}
              </button>
            </div>
            <div className="sg-spread">
              <span className="sg-muted" style={{ fontSize: 12 }}>User Continue Override</span>
              <button className="sg-btn sg-btn-sm" disabled={saving === "allowUserContinueOverride"} onClick={() => toggleSetting("allowUserContinueOverride")}>
                {ws.settings.allowUserContinueOverride ? "Allowed" : "Blocked"}
              </button>
            </div>
            <LockedCategoriesRow ws={ws} saving={saving === "lockedCategories"} onSave={async (next) => {
              setSaving("lockedCategories");
              try {
                await api.updateWorkspaceSettings({ settings: { ...ws.settings, lockedCategories: next } });
                await refresh();
              } finally { setSaving(null); }
            }} />
            <div className="sg-spread">
              <span className="sg-muted" style={{ fontSize: 12 }}>Auto-Generate Safe Version</span>
              <button className="sg-btn sg-btn-sm" disabled={saving === "autoGenerateSafeVersion"} onClick={() => toggleSetting("autoGenerateSafeVersion")}>
                {ws.settings.autoGenerateSafeVersion ? "On" : "Off (User-Initiated)"}
              </button>
            </div>
            <div className="sg-spread">
              <span className="sg-muted" style={{ fontSize: 12 }}>Fail-Closed on Evaluation Error</span>
              <button className="sg-btn sg-btn-sm" disabled={saving === "failClosedOnEvaluationError"} onClick={() => toggleSetting("failClosedOnEvaluationError")}>
                {ws.settings.failClosedOnEvaluationError ? "Block" : "Allow (Default)"}
              </button>
            </div>
            <div className="sg-muted" style={{ fontSize: 12 }}>
              Prompt bodies are encrypted at rest. Reveal actions are logged. When Auto-Generate is off, users click <b>Generate Safe Version</b> on demand — this reduces LLM costs.
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/** Per-category lock list. Categories on this list refuse Send Anyway
 *  even when the workspace permits override globally. Defaults to
 *  ["credentials"] which is also the only one that's genuinely unsafe
 *  to send under any circumstances. */
const LOCKABLE_CATEGORIES: Array<{ id: string; label: string; defaultLocked: boolean }> = [
  { id: "credentials",            label: "Credentials & Secrets",    defaultLocked: true  },
  { id: "pii",                    label: "Personal Information",     defaultLocked: false },
  { id: "legal_client_matter",    label: "Client & Matter Data",     defaultLocked: false },
  { id: "contract_text",          label: "Contract Text",            defaultLocked: false },
  { id: "source_code",            label: "Source Code",              defaultLocked: false },
  { id: "financials",             label: "Financial Data",           defaultLocked: false },
  { id: "internal_project_names", label: "Internal Project Names",   defaultLocked: false },
];

function LockedCategoriesRow({ ws, saving, onSave }: { ws: any; saving: boolean; onSave: (next: string[]) => Promise<void> }) {
  const current: string[] = ws.settings?.lockedCategories ?? ["credentials"];
  return (
    <div style={{ paddingTop: 6, paddingBottom: 4 }}>
      <div className="sg-muted" style={{ fontSize: 12, marginBottom: 8 }}>
        Locked categories (Send Anyway is always refused)
      </div>
      <div className="sg-row" style={{ flexWrap: "wrap", gap: 6 }}>
        {LOCKABLE_CATEGORIES.map((cat) => {
          const locked = current.includes(cat.id);
          return (
            <button
              key={cat.id}
              type="button"
              className={`sg-btn sg-btn-sm ${locked ? "sg-btn-primary" : ""}`}
              disabled={saving}
              title={cat.defaultLocked && !locked ? "Recommended to keep this locked." : undefined}
              onClick={async () => {
                const next = locked ? current.filter((c) => c !== cat.id) : [...current, cat.id];
                await onSave(next);
              }}
            >
              {locked ? "🔒 " : ""}{cat.label}
            </button>
          );
        })}
      </div>
      <div className="sg-muted" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.45 }}>
        Credentials are locked by default. Unlocking is not recommended.
        Categories you lock here cannot be sent even when User Continue Override is allowed.
      </div>
    </div>
  );
}

function ModeCard({ title, body, active, disabled, onClick, kind }: {
  title: string; body: string; active: boolean; disabled: boolean; onClick: () => void;
  kind: "neutral" | "rewrite" | "block";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="sg-panel"
      style={{
        textAlign: "left",
        cursor: "pointer",
        padding: 16,
        borderLeft: `3px solid ${active ? "var(--sg-ink)" : "var(--sg-line-firm)"}`,
        background: active ? "var(--sg-bg-1)" : "var(--sg-bg-0)",
        transition: "background 120ms, border-color 120ms",
      }}
    >
      <div className="sg-row" style={{ justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontWeight: 600, fontSize: 13, letterSpacing: "-0.005em" }}>{title}</span>
        {active && <Pill kind={kind}>Active</Pill>}
      </div>
      <div className="sg-muted" style={{ fontSize: 12, lineHeight: 1.5 }}>{body}</div>
    </button>
  );
}

function Row({ label, value }: { label: string; value: any }) {
  return (
    <div className="sg-spread">
      <span className="sg-muted" style={{ fontSize: 12 }}>{label}</span>
      <span style={{ fontWeight: 500 }}>{value}</span>
    </div>
  );
}
